import React from 'react';

const formatTime = (timeString) => {
  const [hours, minutes] = timeString.split(':');
  const hour24 = parseInt(hours);
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return `${hour12}:${minutes} ${ampm}`;
};

function TimeSlotGrid({
  availability,
  selectedSlots,
  bookingDate,
  onSlotToggle,
  waitingListCounts = {},
  currentBookingSlots = [],
  isMemberUser = false,
  animated = false,
}) {
  if (availability.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading availability...</p>
      </div>
    );
  }

  const now = new Date();
  const today = now.toISOString().split('T')[0];

  const filteredSlots = availability.filter(slot => {
    if (bookingDate !== today) return true;
    const [slotHours, slotMinutes] = slot.slot_time.split(':').map(Number);
    const slotDateTime = new Date();
    slotDateTime.setHours(slotHours, slotMinutes, 0, 0);
    const slotWithGrace = new Date(slotDateTime.getTime() + 20 * 60 * 1000);
    const isPast = now > slotWithGrace;
    if (isPast && slot.is_available && (slot.slot_type_label === 'available' || !slot.slot_type_label)) return false;
    return true;
  }).sort((a, b) => {
    const [aH, aM] = a.slot_time.split(':').map(Number);
    const [bH, bM] = b.slot_time.split(':').map(Number);
    return (aH * 60 + aM) - (bH * 60 + bM);
  });

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-2">
      {filteredSlots.map((slot, index) => {
        let isPast = false;
        if (bookingDate === today) {
          const [slotHours, slotMinutes] = slot.slot_time.split(':').map(Number);
          const slotDateTime = new Date();
          slotDateTime.setHours(slotHours, slotMinutes, 0, 0);
          const slotWithGrace = new Date(slotDateTime.getTime() + 20 * 60 * 1000);
          isPast = now > slotWithGrace;
        }

        const isSelected = selectedSlots.some(s => s.slot_time === slot.slot_time);
        const isCurrent = currentBookingSlots.includes((slot.slot_time || '').substring(0, 5));
        const isMemberOnly = slot.slot_type_label === 'members_only' || (slot.member_only && !isMemberUser);
        const isCoachingHour = slot.slot_type_label === 'coaching_hour';
        const isBookableType = slot.slot_type_label === 'available' || !slot.slot_type_label;
        const canSelect = slot.is_available && isBookableType && !isPast;
        const waitingCount = waitingListCounts[slot.slot_time] || 0;

        const slotCardClass = !slot.is_available
          ? 'slot-booked cursor-not-allowed'
          : isMemberOnly
          ? 'slot-members-only cursor-not-allowed'
          : isCoachingHour
          ? 'slot-coaching-hour cursor-not-allowed'
          : isPast
          ? 'slot-booked cursor-not-allowed'
          : isSelected
          ? 'slot-selected'
          : 'slot-available';

        const animClass = animated ? `animate-fade-in-up animate-delay-${Math.min(index * 100, 500)}` : '';

        let label;
        if (!slot.is_available) label = '❌ Booked';
        else if (isMemberOnly) label = '🔒 Members';
        else if (isCoachingHour) label = '🏟️ Coaching';
        else if (isPast) label = '⏰ Past';
        else if (isSelected) label = '✅ Selected';
        else if (isCurrent) label = '🔄 Current';
        else if (waitingCount > 0) label = `📋 WL-${waitingCount}`;
        else label = '🏸 Available';

        return (
          <button
            key={slot.slot_time}
            onClick={() => canSelect ? onSlotToggle(slot) : null}
            disabled={!canSelect}
            className={`slot-card px-1 sm:px-2 py-1.5 rounded-lg text-sm font-medium h-[62px] sm:h-[68px] flex flex-col justify-between overflow-hidden ${slotCardClass} ${animClass}`}
          >
            <div className="flex-1 flex flex-col justify-center">
              <div className="font-bold text-xs sm:text-sm text-center truncate">{formatTime(slot.slot_time)}</div>
            </div>
            <div className="text-[9px] sm:text-[10px] font-medium text-center truncate">{label}</div>
          </button>
        );
      })}
    </div>
  );
}

export { formatTime };
export default TimeSlotGrid;
